/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable no-console */
import React from "react";
import { Empty, TableProps } from "antd";
import { find, keyBy, mapValues, orderBy } from "lodash";
import { UserOutlined } from "@ant-design/icons";
import moment from "moment";
import { Form, Table, Tooltip, Tag } from "../../atoms";
import {
  IHotelBookingPhysicalRoom,
  IRoomTypeAllotmentPriceList,
} from "../../../common/types";
import {
  hotelBirthdayPromoConfig,
  isHotelBirthdayPromoActive,
} from "../../../common/util/hotelBirthdayPromo";
import { SelectField } from "../commonFields";
import {
  getRoomPricesWithDetails,
  IHotelRoomWithDetails,
} from "../../../firestore/firestoreService/RoomPricePerDayStore";
import { HotelPriceListWrapper } from "./Hotel.style";
import { MealPriceSelection } from "./components/MealPriceSelection";
import { formatPrice } from "./hotelItemHelpers";
import { HotelRoomInfo } from ".";
const partnerId = process.env.REACT_APP_SITE_ID || "";
function createArrayFromNumber(n: number) {
  return Array.from({ length: n }, (_, index) => index);
}
const createGuestIcons = (guestNumber: number) =>
  createArrayFromNumber(guestNumber).map((i: number) => (
    <UserOutlined key={i} />
  ));
const pricingView = (record: IHotelRoomWithDetails) => {
  const rawPrice = Number(record.totalPrice);
  const sellingPrice = Number.isFinite(Number(record.discountPrice))
    ? Number(record.discountPrice)
    : rawPrice;
  const priceLabel = formatPrice(Math.ceil(rawPrice));
  const sellingLabel = formatPrice(Math.ceil(sellingPrice));
  const showSelling =
    Number.isFinite(sellingPrice) &&
    Number.isFinite(rawPrice) &&
    sellingPrice > rawPrice &&
    sellingLabel !== priceLabel;
  return (
    <div className="hotelPriceList-price">
      <div className="price-amounts">
        {showSelling && (
          <div className="price-amount selling">{sellingLabel}</div>
        )}
        <div className="price-amount">{priceLabel}</div>
      </div>
    </div>
  );
};
interface IProps {
  hotelId: string;
  endDate?: string;
  addRoom: (room: IHotelBookingPhysicalRoom) => void;
  removeRoom: (id: string) => void;
  startDate: string;
  adultNumbers: number;
  childrenNumber: number;
  infant: number;
  isMobile: boolean;
  reCall: string;
  selectedRooms: any[];
}
export const HotelPriceList: React.FC<IProps> = ({
  hotelId,
  endDate,
  addRoom,
  removeRoom,
  startDate,
  adultNumbers,
  childrenNumber,
  infant,
  isMobile,
  reCall,
  selectedRooms,
}) => {
  const showBirthdayPromo = isHotelBirthdayPromoActive({
    partnerId,
    hotelId,
  });
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [roomOffers, setRoomOffers] = React.useState<IHotelRoomWithDetails[]>(
    [],
  );
  const requestedNights = React.useMemo(() => {
    if (!startDate) return 0;
    const start = moment(startDate, "YYYY-MM-DD");
    const end = endDate
      ? moment(endDate, "YYYY-MM-DD")
      : start.clone().add(1, "day");
    return Math.max(end.diff(start, "days"), 1);
  }, [startDate, endDate]);
  const formatRoomOffers = React.useCallback(
    (offers: IHotelRoomWithDetails[]) => {
      const sorted = orderBy(
        offers,
        ["displayTotalPrice", "netPrice"],
        ["asc", "asc"],
      );
      return sorted.reduce(
        (acc: IHotelRoomWithDetails[], record: IHotelRoomWithDetails) => {
          // const existing = acc.find((item) => item.id === record.id);
          // if (existing) {
          //   existing.rowSpan += 1;
          //   record.rowSpan = 0;
          // } else {
          //   record.rowSpan = 1;
          // }
          acc.push(record);
          return acc;
        },
        [],
      );
    },
    [],
  );

  React.useEffect(() => {
    if (!hotelId || !startDate) {
      setRoomOffers([]);
      return;
    }
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getRoomPricesWithDetails({
          hotelId,
          fromDate: startDate,
          toDate: endDate || startDate,
          adults: adultNumbers,
          children: childrenNumber,
          infants: +infant || 0,
          partnerId,
        });
        if (!active) return;
        const hotelData = data.find((hotel) => hotel.id === hotelId);
        const offers = (hotelData?.rooms || [])
          .filter((room) => !room.allPricesStopSale && !room.anyStopSale)
          .map(
            (room) =>
              ({
                ...room,
                id: room.id,
                roomId: room.id,
              }) as unknown as IHotelRoomWithDetails,
          );
        setRoomOffers(formatRoomOffers(offers || []));
      } catch (error) {
        console.error("Failed to load room prices", error);
        if (active) setRoomOffers([]);
      }
      if (active) setLoading(false);
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    load();
    return () => {
      active = false;
    };
  }, [
    hotelId,
    startDate,
    endDate,
    adultNumbers,
    childrenNumber,
    infant,
    reCall,
    formatRoomOffers,
  ]);
  const isMinStayBlocked = (record: IHotelRoomWithDetails) =>
    !!record.minStay &&
    requestedNights > 0 &&
    Number(record.minStay) > requestedNights;
  const isFullyBooked = (record: IHotelRoomWithDetails) =>
    (record.availableRoomsCount ?? record.allotment ?? 0) <= 0;
  const renderAvailability = (record: IHotelRoomWithDetails) => {
    const roomCountLine =
      record.availableRoomsCount && record.availableRoomsCount > 1
        ? `Διαθέσιμα ${record.availableRoomsCount} δωμάτια`
        : record.availableRoomsCount === 1
          ? "Διαθέσιμο 1 δωμάτιο"
          : "";
    if (isMinStayBlocked(record)) {
      return (
        <>
          <Tag color="red">Ελάχιστη διαμονή {record.minStay} νύχτες</Tag>
          <div className="hotelPriceList-minStay">
            Για τις ημερομηνίες που έχετε επιλέξει, η ελάχιστη διάρκεια διαμονής
            στο συγκεκριμένο δωμάτιο είναι {record.minStay} διανυκτερεύσεις. Αν
            επιθυμείτε αυτό το τύπο δωματίου θα πρέπει να επιλέξετε δισμονή για
            τουλάχιστον {record.minStay} βράδια.
          </div>
          {roomCountLine && (
            <div className="meta-highlight">{roomCountLine}</div>
          )}
        </>
      );
    }
    if (isFullyBooked(record)) {
      return (
        <>
          <Tag color="orange">On request</Tag>
          <div className="hotelPriceList-onRequest">
            Το συγκεριμένο δωμάτιο δεν παρέχει άμεση διαδικτυακή επιβεβαίωση για
            τις συγκεκριμένες ημερομηνίες. Μπορείτε να μας στείλετε το αίτημα
            σας χωρίς καμία δέσμευση επιλέγοντας την On Request προσφορά.
          </div>
        </>
      );
    }
    if (record.onRequest) {
      return (
        <>
          <Tag color="orange">On request</Tag>
          {roomCountLine && (
            <div className="meta-highlight">{roomCountLine}</div>
          )}
        </>
      );
    }
    if (record?.allotment !== undefined && record.allotment <= 5) {
      return (
        <>
          {roomCountLine && (
            <div className="meta-highlight">{roomCountLine}</div>
          )}
        </>
      );
    }
    return (
      <>
        {roomCountLine && <div className="meta-highlight">{roomCountLine}</div>}
      </>
    );
  };
  const renderEarlyBooking = (record: IHotelRoomWithDetails) => {
    if (!record.hasEarlyBookingDiscount) return null;
    return (
      <div className="hotelPriceList-early-discount">
        Εξοικονόμηση Early Booking Discount
      </div>
    );
  };
  const renderDayExtras = (record: IHotelRoomWithDetails) => {
    const extras = (record as any).dayExtrasDescriptions as
      | string[]
      | undefined;
    if (!Array.isArray(extras) || extras.length === 0) return null;
    return (
      <div className="hotelPriceList-day-extras">
        {extras.map((text, index) => (
          <div
            key={`${record.id}-day-extra-${index}`}
            className="hotelPriceList-day-extra"
          >
            {text}
          </div>
        ))}
      </div>
    );
  };
  const handleAddRoomToCart = (record: IHotelRoomWithDetails) => async () => {
    console.log("Adding room to cart:", record);
    const values = await form.validateFields();
    Object.entries(values).forEach(([key, value]) => {
      if (
        key !== record.id &&
        (value as { rooms?: number })?.rooms !== undefined
      ) {
        form.setFieldValue([key, "rooms"], 0);
        removeRoom?.(key);
      }
    });
    const mealsPrice =
      find(record.mealsPrices, ["name", values[record.id].mealType])?.price ||
      0;
    const isOnRequest = record.onRequest || isFullyBooked(record);
    if (+values[record.id].rooms > 0) {
      addRoom({
        id: record.id,
        roomId: record.id,
        adultNumber: record.adults || 0,
        childNumber: record.childs || 0,
        infantNumber: +infant,
        totalPrice:
          Math.ceil(+record.totalPrice + +mealsPrice) *
          +values[record.id].rooms,
        sellPrice:
          Math.ceil(
            (+record.discountPrice || +record.totalPrice) + +mealsPrice,
          ) * +values[record.id].rooms,
        netPrice:
          Math.ceil(+(record.totalNetPrice ?? 0) + +mealsPrice) *
          +values[record.id].rooms,
        roomNumber: +values[record.id].rooms,
        mealPrice: +mealsPrice,
        roomPrice: +record.totalPrice,
        mealType: values[record.id]?.mealType || record.mealsType || "",
        dayExtrasDescriptions: record.dayExtrasDescriptions || [],
        refundableDate: record.refundableDate || "",
        cancelationPolicy: record.cancelationPolicy || "",
        fullPaymentDueDate: record.fullPaymentDueDate || "",
        mainGuest: null,
        onRequest: isOnRequest,
        allowPartialPayment:
          record.allowPartialPayment ??
          (moment(record.fullPaymentDueDate) > moment() &&
            record.cancelationPolicy === "REFUNDABLE"),
        downPaymentAmount: record.downPaymentAmount,
      });
    } else {
      removeRoom(record.id);
    }
  };

  React.useEffect(() => {
    if (!roomOffers.length) return;
    const fields = roomOffers.reduce(
      (acc, room) => {
        acc[room.id] = { rooms: 0 };
        return acc;
      },
      {} as Record<string, { rooms: number }>,
    );
    if (selectedRooms?.length) {
      const selectedId = selectedRooms[0]?.id;
      if (selectedId) {
        fields[selectedId] = { rooms: 1 };
      }
    }
    form.setFieldsValue(fields);
  }, [roomOffers, selectedRooms, form]);

  const detailsColumns: TableProps<any>["columns"] = [
    {
      title: "Αριθμός Δωματίων",
      width: 150,
      render: (_: string, record) => (
        <>
          <Tooltip
            title={`${record.adults} ${record.adults === 1 ? "Ενήλικας" : "Ενήλικες"} ${record.childs || ""}
          ${(record.childs === 1 && "Παιδί") || ""}  ${(record.childs > 1 && "Παιδιά") || ""}`}
          >
            {createGuestIcons(record.adults)}{" "}
            <span style={{ fontSize: 11 }}>
              {createGuestIcons(record.childs)}
            </span>
          </Tooltip>
          {pricingView(record)}
          <div className="hotelPriceList-availability">
            {renderAvailability(record)}
          </div>
          {renderEarlyBooking(record)}
          <MealPriceSelection
            offer={record}
            recordId={record.id}
            addRoom={handleAddRoomToCart(record)}
          />
          {renderDayExtras(record)}
          <SelectField
            label={"Αριθμός Δωματίων"}
            name={[record.id, "rooms"]}
            onChange={handleAddRoomToCart(record)}
            style={{ width: 120 }}
            disabled={isMinStayBlocked(record)}
            options={[
              { value: 0, label: "0" },
              { value: 1, label: "1" },
            ]}
          />
          {showBirthdayPromo && (
            <img
              className="hotelPriceList-promo-image"
              src={hotelBirthdayPromoConfig.imagePath}
              alt="Birthday Offer -9%"
            />
          )}
        </>
      ),
    },
  ];
  const fixedColumns: TableProps<any>["columns"] = [
    {
      title: "Δωμάτιο",
      width: 300,
      dataIndex: "roomId",
      render: (roomId: string, record) => (
        <div>
          <HotelRoomInfo
            roomId={roomId}
            hotelId={hotelId}
            onRequest={record.onRequest || isFullyBooked(record)}
            roomAvailable={record.roomAvailable}
          />
          {isMobile && (
            <Table
              columns={detailsColumns}
              rowKey="id"
              dataSource={roomOffers.filter((r) => r.id === roomId)}
              pagination={false}
              loading={loading}
            />
          )}
        </div>
      ),
      onCell: (record) => ({
        rowSpan: record.rowSpan,
        colSpan: isMobile && record.rowSpan === 1 ? 4 : 1,
        className: record.rowSpan === 1 ? "hotelPriceList-roomCol" : "",
      }),
      align: "left",
      className: "hotelPriceList-roomCol",
    },
    {
      title: "Αριθμός Επισκεπτών",
      dataIndex: "title",
      responsive: ["sm"],
      width: 150,
      render: (_: string, record) => (
        <Tooltip
          title={`${record.adults} ${record.adults === 1 ? "Ενήλικας" : "Ενήλικες"} ${record.childs || ""}
          ${(record.childs === 1 && "Παιδί") || ""}  ${(record.childs > 1 && "Παιδιά") || ""}  ${+infant === 1 ? "1 βρέφος" : ""}`}
        >
          {createGuestIcons(record.adults)}{" "}
          <span style={{ fontSize: 11 }}>
            {createGuestIcons(record.childs)}
            {+infant === 1 && (
              <img
                src="/assets/baby-boy.png"
                alt=""
                style={{ height: 12, marginLeft: 5 }}
              />
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Τιμή",
      dataIndex: "totalPrice",
      responsive: ["sm"],
      width: 150,
      render: (_price: string, record) => <>{pricingView(record)}</>,
    },
    {
      title: "Πολιτική Δωματίου",
      dataIndex: "mealsTypePrices",
      responsive: ["sm"],
      render: (_: string, record) => (
        <>
          <MealPriceSelection
            offer={record}
            recordId={record.id}
            addRoom={handleAddRoomToCart(record)}
          />
          <div className="hotelPriceList-availability">
            {renderAvailability(record)}
          </div>
          {renderEarlyBooking(record)}
          {renderDayExtras(record)}
        </>
      ),
    },
    {
      title: "Αριθμός Δωματίων",
      width: 150,
      responsive: ["sm"],
      render: (_: string, record) => (
        <>
          <SelectField
            label={""}
            name={[record.id, "rooms"]}
            onChange={handleAddRoomToCart(record)}
            style={{ width: 120 }}
            disabled={isMinStayBlocked(record)}
            options={[
              { value: 0, label: "0" },
              { value: 1, label: "1" },
            ]}
          />
          {showBirthdayPromo && (
            <img
              className="hotelPriceList-promo-image"
              src={hotelBirthdayPromoConfig.imagePath}
              alt="Birthday Offer -9%"
            />
          )}
        </>
      ),
    },
  ];
  React.useEffect(() => form.resetFields, [roomOffers]);
  return (
    <HotelPriceListWrapper>
      {" "}
      <Form
        form={form}
        name="horizontal_login"
        autoComplete="off"
        initialValues={mapValues(
          keyBy(roomOffers, "id"),
          (item: IRoomTypeAllotmentPriceList) => ({
            rooms: 0,
            mealType: item.mealsType,
          }),
        )}
      >
        {!loading && roomOffers.length === 0 && (
          <Empty description={"Ξενοδοχείο Πλήρες"}>
            Δυστυχώς το Ξενοδοχείο είναι πλήρης για τις ημερομηνίες που
            αναζητάτε. Παρακαλώ επιλέξτε νέα ημερομηνία για να προχωρήσετε με
            κράτηση.
          </Empty>
        )}

        <Table
          columns={fixedColumns}
          rowKey="id"
          dataSource={roomOffers}
          pagination={false}
          loading={loading}
        />
      </Form>
    </HotelPriceListWrapper>
  );
};
